import { useCallback, useMemo, useState } from 'react';
import { entropyToMnemonic } from 'bip39';
import type { EthersError } from 'ethers';
import keccak256 from 'keccak256';
import { useSnackbar } from 'notistack';
import type { PopoverProps } from '@mui/material';
import { Typography } from '@mui/material';

import { RPC_ERROR_MESSAGE } from 'src/constants/error';
import { MAINNET } from 'src/constants/network';
import { useCurrentAccount } from 'src/hooks/useAccount';
import { useCurrentWallet } from 'src/hooks/useCurrentWallet';
import useProvider from 'src/hooks/useProivder';
import type { RPCError } from 'src/types/error';
import type { WalletInfo } from 'src/types/wallet';
import { extractCleanSignatureHash, getAddress, mnemonicToPair } from 'src/utils/crypto';

import {
  Base02TextContainer,
  BodyContainer,
  BodyItemContainer,
  Container,
  FooterContainer,
  IconContainer,
  ProgressContainer,
  ProgressLeftTextContainer,
  StyledCircularProgress,
  StyledPopover,
  SuccessIconContainer,
  TopContainer,
  TopLeftContainer,
  WrapperContainer,
} from './styled';
import GradientButton from '../GradientButton';
import IconButton from '../IconButton';

import { ReactComponent as Close32Icon } from 'src/assets/svg/Close32.svg';
import { ReactComponent as EmptyCircleIcon } from 'src/assets/svg/EmptyCircle.svg';
import { ReactComponent as SuccessIcon } from 'src/assets/svg/TxSuccess.svg';

type GetDYDXPopoverProps = Omit<PopoverProps, 'children'> & {
  currentExtensionNetworkId?: string;
  wallet: WalletInfo;
};

export default function GetDYDXPopover({ currentExtensionNetworkId, onClose, wallet, ...remainder }: GetDYDXPopoverProps) {
  const { setCurrentWallet } = useCurrentWallet();

  const { enqueueSnackbar } = useSnackbar();
  const { setCurrentAccount } = useCurrentAccount();

  const [isButtonDisabled, setIsButtonDisabled] = useState(false);

  const [isChangeNetworkRequest, setIsChangeNetworkRequest] = useState(false);

  const [isSuccessConnectWallet, setIsSuccessConnectWallet] = useState(false);

  const { provider } = useProvider();
  const { currentAccount } = useCurrentAccount();

  const isMainnet = useMemo(() => currentExtensionNetworkId === MAINNET.hexChainId, [currentExtensionNetworkId]);

  const currentExtensionNetworkIdNumber = useMemo(() => {
    if (isMainnet) return MAINNET.chainId;

    return 0;
  }, [isMainnet]);

  const requestChangeNetworkWallet = useCallback(
    async (chainId: string) => {
      try {
        setIsChangeNetworkRequest(true);
        await provider?.send('wallet_switchEthereumChain', [{ chainId }]);
        setIsChangeNetworkRequest(false);
      } catch (e) {
        const etherError = e as EthersError;
        const etherErrorInfo = etherError.info?.error as RPCError;

        setIsChangeNetworkRequest(false);
        enqueueSnackbar('Fail to Change Network', {
          variant: 'error',
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          errorMsg: RPC_ERROR_MESSAGE[etherErrorInfo.code] || '',
          autoHideDuration: 3000,
        });
      }
    },
    [enqueueSnackbar, provider],
  );

  const bottomButtonText = useMemo(() => {
    if (!currentAccount.mnemonic && !isMainnet) return 'SWITCH NETWORK TO ETHEREUM MAINNET';

    if (!currentAccount.mnemonic && isMainnet) return 'SEND REQUEST';

    return '';
  }, [currentAccount.mnemonic, isMainnet]);

  return (
    <WrapperContainer data-is-open={remainder.open}>
      <StyledPopover onClose={onClose} {...remainder}>
        <Container>
          <TopContainer>
            <TopLeftContainer>
              <Typography variant="h2">Sign Message</Typography>
            </TopLeftContainer>
            <IconButton
              onClick={() => {
                onClose?.({}, 'backdropClick');
              }}
            >
              <IconContainer>
                <Close32Icon />
              </IconContainer>
            </IconButton>
          </TopContainer>

          <Base02TextContainer>
            <Typography variant="h5">
              Signature are used to verify your ownership and to confirm wallet compatibility. New users will receive two signature requests.
            </Typography>
          </Base02TextContainer>

          <BodyContainer>
            <BodyItemContainer>
              <ProgressContainer>
                {isButtonDisabled && !isChangeNetworkRequest ? (
                  <StyledCircularProgress size={53.5} />
                ) : isSuccessConnectWallet ? (
                  <SuccessIconContainer>
                    <SuccessIcon />
                  </SuccessIconContainer>
                ) : (
                  <EmptyCircleIcon />
                )}
                <ProgressLeftTextContainer>
                  <Typography variant="h4">Generate your dYdX Chain wallet</Typography>
                </ProgressLeftTextContainer>
              </ProgressContainer>
            </BodyItemContainer>
          </BodyContainer>

          <GradientButton
            disabled={isButtonDisabled}
            onClick={async () => {
              setIsButtonDisabled(true);
              try {
                if (!isMainnet) {
                  await requestChangeNetworkWallet(MAINNET.hexChainId);
                } else if (!currentAccount.mnemonic) {
                  const signedSignature = (await provider?.send('eth_signTypedData_v4', [
                    currentAccount.accAddress,
                    `{"domain":{"name":"dYdX Chain","chainId":${currentExtensionNetworkIdNumber}},"primaryType":"dYdX","types":{"EIP712Domain":[{"name":"name","type":"string"},{"name":"chainId","type":"uint256"}],"dYdX":[{"name":"action","type":"string"}]},"message":{"action":"dYdX Chain Onboarding"}}`,
                  ])) as string;

                  const cleanedSignature = extractCleanSignatureHash(signedSignature);

                  const bufferHex = Buffer.from(cleanedSignature, 'hex');

                  const subBufferHex = bufferHex.subarray(0, 64);

                  const hash = keccak256(subBufferHex);

                  const mnemonic = entropyToMnemonic(hash);

                  const keypair = mnemonicToPair(mnemonic);

                  const dydxAddress = getAddress(keypair.publicKey);

                  setCurrentAccount({
                    ...currentAccount,
                    mnemonic,
                    cosmosAddress: dydxAddress,
                  });
                  setIsSuccessConnectWallet(true);
                  onClose?.({}, 'backdropClick');

                  setCurrentWallet(wallet);
                  setIsSuccessConnectWallet(false);
                }
              } catch (e) {
                const etherError = e as EthersError;
                const etherErrorInfo = etherError.info?.error as RPCError;

                enqueueSnackbar('Connect wallet failed', {
                  variant: 'error',
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  errorMsg: RPC_ERROR_MESSAGE[etherErrorInfo.code] || '',
                  autoHideDuration: 3000,
                });
              } finally {
                setIsButtonDisabled(false);
                setIsSuccessConnectWallet(false);
              }
            }}
          >
            {isChangeNetworkRequest ? <StyledCircularProgress size={20} /> : <Typography variant="h4"> {bottomButtonText}</Typography>}
          </GradientButton>
          <FooterContainer>
            <Base02TextContainer>
              <Typography variant="h7">Signing is free and will not send a transaction.</Typography>
            </Base02TextContainer>
          </FooterContainer>
        </Container>
      </StyledPopover>
    </WrapperContainer>
  );
}
